import axios, { AxiosError } from "axios";
import { v4 as uuidv4 } from "uuid";
import { ConsentAction, getStorageMechanism, } from "@finarkein/aasdk-core";
import { getConsentRequestDetailsMapped, getMappedLinkedAccounts, handleErrors, isFIPAvailable, } from "./operations";
import { ResponseMapperOnemoney } from "./utils/ResponseMapperOnemoney";
import { EncryptService } from "./utils/EncryptService";
import { Routes } from "./utils/RouteConstants";
export class OnemoneyService {
    constructor() {
        this.availableStorage = getStorageMechanism();
        this.xApiKey = "";
        this.timeout = 45000;
        this.noOfRetries = 5;
        this.delayTimeout = 3000;
        this.linkAccountsMap = new Map();
        this.sendRawResponse = false;
        this.listOfFips = [];
        this.consentDetails = new Map();
        this.identifier = () => "onemoney";
    }
    // This funciton is used to get the payload encryption key from onemoney
    async getBrowserInfoData() {
        var _a;
        const randomNumber = uuidv4();
        await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}/user/browser/info?v1=${Math.random() * 999999999999999}`, {
            headers: {
                "accept": "application/json, text/plain, */*",
                "accept-language": "en-US,en;q=0.9",
                "content-type": "application/json",
                "ominfokey": randomNumber,
            }
        }).then(response => {
            const value = response.data;
            if (value.encryptedOmInfoKey && this.encryptService) {
                const decrypt = this.encryptService.decryptUsingAES256(value.encryptedOmInfoKey, randomNumber);
                const decryptedObject = JSON.parse(decrypt);
                const payloadEncryptionKey = decryptedObject.paylodEncryptionKey;
                this.encryptService.setEncryptionKey(JSON.stringify(payloadEncryptionKey));
            }
        }));
    }
    async setup(initOptions) {
        this.baseUrl = initOptions.url;
        this.encryptService = new EncryptService();
        if (initOptions.rawResponse) {
            this.sendRawResponse = true;
        }
        if (initOptions.timeout) {
            this.timeout = +initOptions.timeout;
        }
        if (initOptions.count_of_retries) {
            this.noOfRetries = +initOptions.count_of_retries;
        }
        if (initOptions.delay_between_retries) {
            this.delayTimeout = +initOptions.delay_between_retries;
        }
        this.http = axios.create({
            baseURL: initOptions.url,
            headers: {
                Accept: "application/json",
                "Content-type": "application/json",
            },
            timeout: this.timeout,
            withCredentials: false,
        });
        await this.getBrowserInfoData();
        this.http.interceptors.request.use(async (config) => {
            var _a, _b;
            const headers = config.headers;
            const body = config.data;
            // for all post request encrypt the data
            if (body && !((_a = config === null || config === void 0 ? void 0 : config.url) === null || _a === void 0 ? void 0 : _a.includes("webRedirection/Decrypt"))) {
                const encryptData = (_b = this.encryptService) === null || _b === void 0 ? void 0 : _b.payload_encode(body);
                config.data = {
                    "encryptedData": encryptData
                };
            }
            headers.set("sessionid", this.resolveAccessToken());
            return config;
        }, (error) => Promise.reject(error));
        return new Promise((resolve) => resolve());
    }
    resolveAccessToken() {
        return this.availableStorage.getValue("sessionId")
            ? this.availableStorage.getValue("sessionId")
            : null;
    }
    async initiateLogin(input) {
        const request = {
            phone_number: input.mobileNum,
        };
        let retryCount = 0;
        const attemptRequest = () => {
            var _a;
            return (_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.SEND_LOGIN_OTP}`, request).then((response) => {
                if (response.status === 200) {
                    this.availableStorage.setValue("userId", request.phone_number);
                    this.availableStorage.setValue("isUserRegistered", true);
                    return {
                        statusCode: response.status,
                        status: "SEND",
                        otpReference: response.data.otp_reference,
                    };
                }
            }).catch(async (error) => {
                var _a, _b, _c;
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 404 || ((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.errorCode) === "USER_NOT_FOUND") {
                    return this.initiateSignup(input);
                }
                return handleErrors(error, "VALIDATE_USER");
            });
        };
        return attemptRequest();
    }
    async initiateSignup(input) {
        const request = {
            // TODO Confirm about this what should be the value
            username: "Customer",
            vua: `${input.mobileNum}@onemoney`,
            phone_number: input.mobileNum,
            "termsAndConditions": true,
        };
        let retryCount = 0;
        const attemptRequest = () => {
            var _a;
            return (_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.SEND_SIGNUP_OTP}`, request).then((response) => {
                this.availableStorage.setValue("userId", input.mobileNum);
                this.availableStorage.setValue("isUserRegistered", false);
                return {
                    statusCode: response.status,
                    status: "SEND",
                    otpReference: response.data.userID,
                };
            }).catch(async (error) => {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    handleErrors(error, "VALIDATE_USER");
                }
            });
        };
        return attemptRequest();
    }
    async authInit(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            let _aaOperation = "";
            let promise;
            try {
                if (input.mobileNum) {
                    _aaOperation = "ONEMONEY_AUTH_MOBILE_CUSTOMERS-FIU-LOGIN";
                    promise = this.initiateLogin(input);
                }
                else {
                    const request = {
                        webRedirectionURL: {
                            fi: input.encryptedFiuId,
                            ecreq: input.encryptedRequest,
                            reqdate: input.requestDate,
                        }
                    };
                    _aaOperation = "ONEMONEY_AUTH_WEBVIEW_CUSTOMERS-FIU-VALIDATE";
                    promise = (_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.DECODE}`, request).then(async (response) => {
                        var _a;
                        const data = response.data || {};
                        const mobileNumber = (_a = data.userid) === null || _a === void 0 ? void 0 : _a.split("@")[0];
                        this.availableStorage.setValue("userId", data.userid);
                        this.availableStorage.setValue("isUserRegistered", data.isUserRegistered);
                        return await data.isUserRegistered ? this.initiateLogin({ mobileNum: mobileNumber }) : this.initiateSignup({ mobileNum: mobileNumber });
                    });
                }
                return await promise;
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, _aaOperation); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("VALIDATE_USER");
    }
    async authVerify(input) {
        const login = Routes.VERIFY_OTP;
        const register = Routes.SIGNUP_VERIFY_OTP;
        let finalUrl = "";
        let type = "";
        let customerReq = {};
        const isUserRegistered = this.availableStorage.getValue("isUserRegistered");
        if (isUserRegistered === "true" || isUserRegistered === true) {
            customerReq = {
                "phone_number": this.availableStorage.getValue("userId"),
                otp_reference: input.otpReference,
                code: input.otp,
                deviceToken: "null"
            };
            finalUrl = login;
            type = "AA_LOGIN";
        }
        else {
            customerReq = {
                username: this.availableStorage.getValue("userId"),
                code: input.otp,
            };
            finalUrl = register;
            type = "AA_REGISTER";
        }
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            const aaOperation = type === "AA_REGISTER"
                ? "ONEMONEY_AUTH_CUSTOMERS-FIU-REGISTER"
                : "ONEMONEY_AUTH_CUSTOMERS-FIU-LOGIN";
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${finalUrl}`, customerReq));
                await this.getFIPList();
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    //store customer id in session storage
                    this.availableStorage.setValue("customerId", response.data.vua);
                    this.availableStorage.setValue("sessionId", response.data.sessionId);
                    return {
                        status: "SUCCESS",
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, aaOperation); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("AA_LOGIN");
    }
    /* OneMoney has a different approach for handling alternate mobile numbers.
     It doesn't provide a separate API for adding an alternate mobile number. Instead,
     the discovery call itself throws an error 'IDENTIFIER_NOT_VERIFIED' if the user is not verified,
     and an OTP is sent to the user.
     To maintain consistency with other SDKs, we're calling the discovery in `authInit`.
     If the user is not registered, they will receive an OTP */
    async mobileAuthInit(input) {
        let retryCount = 0;
        const ids = [{
                type: "MOBILE",
                value: input.mobileNum
            }];
        const fipList = await this.getFIPList();
        const request = {
            fipID: fipList[0].fipID,
            fips: [{ data: ids }],
        };
        const attemptRequest = async () => {
            var _a;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.DISCOVER_ACCOUNTS}`, request));
                if ((response === null || response === void 0 ? void 0 : response.status) === 201 || (response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "ALREADY_REGISTERED",
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_MOBILE-AUTH-INIT"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("ALT_MOBILE");
    }
    async mobileAuthVerify(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const confirmRequest = {
                    "actionType": "VERIFY_IDENTIFIER",
                    "identifierValue": input.mobileNum,
                    "identifierType": "MOBILE",
                    OTP: input.otp,
                };
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.IDENTIFIERS_VERIFY}`, confirmRequest));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "ACCEPT",
                        message: "Mobile Authentication Successful",
                        data: response.data,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_AUTH_MOBILE-VALIDATIONS-RESPONSE"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("CONFIRM_ALT_MOBILE");
    }
    async getLinkedAccounts() {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}${Routes.LINKED_ACCOUNTS}`));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "SUCCESS",
                        LinkedAccounts: getMappedLinkedAccounts(response.data.bankAccounts),
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_LINKED-ACCOUNTS"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("LINKED_ACCOUNTS");
    }
    async discoverAccounts(input) {
        var _a, _b;
        let retryCount = 0;
        const ids = [];
        (_a = input.Customer) === null || _a === void 0 ? void 0 : _a.Identifiers.forEach((data) => {
            ids.push({
                type: data.type,
                value: data.value
            });
        });
        const request = {
            fipID: (_b = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _b === void 0 ? void 0 : _b.fipId,
            fips: [{ data: ids }],
        };
        const attemptRequest = async () => {
            var _a, _b;
            try {
                // If Fip is available with AA call discovery else return error
                if (isFIPAvailable(await this.getFIPList(), `${request.fipID}`)) {
                    const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.DISCOVER_ACCOUNTS}`, request));
                    if ((response === null || response === void 0 ? void 0 : response.status) === 201 || (response === null || response === void 0 ? void 0 : response.status) === 200) {
                        return {
                            status: "SUCCESS",
                            DiscoveredAccounts: ((_b = response === null || response === void 0 ? void 0 : response.data) === null || _b === void 0 ? void 0 : _b.DiscoveredAccounts) || [],
                            statusCode: response === null || response === void 0 ? void 0 : response.status,
                        };
                    }
                }
                return {
                    errorCode: "FIP_NOT_FOUND"
                };
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_ACCOUNTS-DISCOVER"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("ACCOUNT_DISCOVERY");
    }
    async linkAccounts(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
            try {
                const fipId = (_a = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _a === void 0 ? void 0 : _a.fipId;
                const request = {
                    fip: { FIPName: (_b = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _b === void 0 ? void 0 : _b.fipName, FIPID: (_c = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _c === void 0 ? void 0 : _c.fipId },
                    discoveredAccounts: (_e = (_d = input === null || input === void 0 ? void 0 : input.Customer) === null || _d === void 0 ? void 0 : _d.Accounts) === null || _e === void 0 ? void 0 : _e.map(acc => ({
                        "FIType": acc.FIType,
                        "accType": acc.accType,
                        "accRefNumber": acc.accRefNumber,
                        "maskedAccNumber": acc.maskedAccNumber,
                        "linkRequested": true
                    })),
                };
                this.availableStorage.setValue("linkingFip", fipId);
                const linkedAccountsIds = ((_h = (_g = (_f = input === null || input === void 0 ? void 0 : input.Customer) === null || _f === void 0 ? void 0 : _f.Accounts) === null || _g === void 0 ? void 0 : _g.map) === null || _h === void 0 ? void 0 : _h.call(_g, acc => acc.accRefNumber)) || [];
                this.linkAccountsMap.set(fipId, linkedAccountsIds);
                const response = await ((_j = this.http) === null || _j === void 0 ? void 0 : _j.post(`${this.baseUrl}${Routes.LINK_ACCOUNTS}`, request));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "SUCCESS",
                        AuthenticatorType: (_l = (_k = response.data) === null || _k === void 0 ? void 0 : _k.data) === null || _l === void 0 ? void 0 : _l.AuthenticatorType,
                        RefNumber: (_o = (_m = response.data) === null || _m === void 0 ? void 0 : _m.data) === null || _o === void 0 ? void 0 : _o.RefNumber,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_ACCOUNTS-LINK"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("ACCOUNT_LINKING");
    }
    async confirmLinking(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a, _b, _c;
            try {
                const fipId = this.availableStorage.getValue("linkingFip");
                const req = {
                    otp: input.token,
                    RefNumber: input.AccountsLinkingRefNumber,
                    fipID: fipId
                };
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.CONFIRM_LINKING}`, req));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    this.availableStorage.removeValue("linkingFip");
                    // Fetch linkedAccounts to get the linkedRefNumber;
                    const linkedAccounts = await this.getLinkedAccounts();
                    const accLinkDetails = (_c = (_b = linkedAccounts.data) === null || _b === void 0 ? void 0 : _b.LinkedAccounts) === null || _c === void 0 ? void 0 : _c.filter(acc => { var _a, _b; return (_b = (_a = this.linkAccountsMap) === null || _a === void 0 ? void 0 : _a.get(fipId)) === null || _b === void 0 ? void 0 : _b.includes(acc.accRefNumber); });
                    this.linkAccountsMap = new Map();
                    return {
                        status: "SUCCESS",
                        AccLinkDetails: accLinkDetails,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_ACCOUNTS-CONFIRM-LINK"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("CONFIRM_ACCOUNT_LINKING");
    }
    async consentRequestDetails(consentHandle) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}${Routes.CONSENT_REQUEST_DETAILS}/${consentHandle}`));
                const data = (response === null || response === void 0 ? void 0 : response.data) || {};
                const consentMapped = getConsentRequestDetailsMapped(data);
                this.consentDetails.set(consentHandle, consentMapped);
                return {
                    ...consentMapped,
                    statusCode: response === null || response === void 0 ? void 0 : response.status
                };
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_GET_CONSENT-REQUESTS"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("CONSENT_DETAILS");
    }
    async consent(input) {
        let retryCount = 0;
        let accounts = [];
        const type = input.handleStatus === ConsentAction.ACCEPT
            ? "CONSENT_ACTION_APPROVE"
            : "CONSENT_ACTION_REJECT";
        const consentDetails = this.consentDetails.get(input.consentHandleId);
        const attemptRequest = async () => {
            var _a, _b;
            try {
                if (input.handleStatus === ConsentAction.ACCEPT) {
                    accounts = input.FIPDetails.flatMap(obj => obj.Accounts.map(account => ({
                        "userAccountID": account.id,
                        "fipID": account.fipId,
                        "fiType": account.FIType,
                        "accountType": account.accType,
                        "accountRefNumber": account.accRefNumber,
                        "maskedAccountNumber": account.maskedAccNumber,
                        "linkRefNumber": account.linkRefNumber,
                        "accessAllowed": true
                    })));
                    const request = {
                        refEventId: input.consentHandleId,
                        accounts,
                        consentTypes: consentDetails.consentTypes,
                        FIDataRange: consentDetails.DataDateTimeRange,
                        consentStart: consentDetails.startTime,
                        consentExpiry: consentDetails.expireTime,
                        Frequency: consentDetails.Frequency,
                    };
                    const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}${Routes.CONSENT_APPROVAL}`, request));
                    if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                        return {
                            status: "SUCCESS",
                            message: "Consent Stored",
                            statusCode: response === null || response === void 0 ? void 0 : response.status,
                        };
                    }
                }
                else {
                    const rejectReq = {
                        refEventId: input.consentHandleId,
                        accounts: [],
                        consentTypes: consentDetails.consentTypes,
                        FIDataRange: consentDetails.DataDateTimeRange,
                        consentStart: consentDetails.startTime,
                        consentExpiry: consentDetails.expireTime,
                        Frequency: consentDetails.Frequency,
                    };
                    const response = await ((_b = this.http) === null || _b === void 0 ? void 0 : _b.post(`${this.baseUrl}${Routes.CONSENT_APPROVAL}`, rejectReq));
                    if ((response === null || response === void 0 ? void 0 : response.status) === 200 || (response === null || response === void 0 ? void 0 : response.status) === 201) {
                        return {
                            status: "SUCCESS",
                            message: "Consent Stored",
                            statusCode: response === null || response === void 0 ? void 0 : response.status,
                        };
                    }
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_" + type); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("CONSENT_ACTION");
    }
    async logout() {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const sessionId = this.availableStorage.getValue("sessionId");
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}/User/sign-out?sessionId=${sessionId}`));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    this.cleanUp();
                    return {
                        statusCode: response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries || error.code === AxiosError.ERR_NETWORK) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_INDIVIDUAL_LOGOUT"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("LOGOUT");
    }
    async getUserInfo() {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a, _b, _c, _d, _e;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}${Routes.USER_INFO}`));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "ACCEPT",
                        UserInfo: {
                            mobileNo: (_c = (_b = response.data) === null || _b === void 0 ? void 0 : _b.userData) === null || _c === void 0 ? void 0 : _c.mobile,
                            userId: (_e = (_d = response.data) === null || _d === void 0 ? void 0 : _d.userData) === null || _e === void 0 ? void 0 : _e.vuaId,
                        },
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries || error.code === AxiosError.ERR_NETWORK) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "ONEMONEY_INDIVIDUAL_PROFILE"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperOnemoney(attemptRequest()).getResponse("USER_INFO");
    }
    cleanUp() {
        var _a;
        this.linkAccountsMap = new Map();
        if (this.availableStorage) {
            (_a = this.availableStorage) === null || _a === void 0 ? void 0 : _a.removeAllValues();
        }
    }
    async getFIPList() {
        var _a;
        try {
            if (Array.isArray(this.listOfFips) && this.listOfFips.length > 0) {
                return this.listOfFips;
            }
            const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}${Routes.FIP_LIST}`));
            const { data, status } = response || {};
            if (status === 200 && Array.isArray(data.fipList)) {
                this.listOfFips = data.fipList.filter((fip) => fip.fipStatus === "ENABLED");
            }
            return this.listOfFips;
        }
        catch (error) {
            console.error(error);
            return [];
        }
    }
}
//# sourceMappingURL=aasdk-onemoney.js.map